import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const LightboxContext = createContext(null);

export function Watermark({ className = "" }) {
  return (
    <span
      aria-hidden="true"
      data-testid="brand-watermark"
      className={`pointer-events-none absolute bottom-1.5 right-2 z-10 select-none font-display text-[9px] sm:text-[11px] font-bold tracking-[0.2em] text-white ${className}`}
      style={{ opacity: 0.35, mixBlendMode: "difference" }}
    >
      HAQILA
    </span>
  );
}

export function LightboxProvider({ children }) {
  const [state, setState] = useState(null);
  const touchStartX = useRef(null);

  const open = useCallback((images, index = 0) => setState({ images, index }), []);
  const close = useCallback(() => setState(null), []);
  const next = useCallback(
    () => setState((s) => (s ? { ...s, index: (s.index + 1) % s.images.length } : s)),
    []
  );
  const prev = useCallback(
    () => setState((s) => (s ? { ...s, index: (s.index - 1 + s.images.length) % s.images.length } : s)),
    []
  );

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [state, close, next, prev]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const endX = e.changedTouches?.[0]?.clientX;
    if (endX === undefined) return;
    const dx = endX - touchStartX.current;
    if (dx > 50) prev();
    else if (dx < -50) next();
    touchStartX.current = null;
  };

  const current = state ? state.images[state.index] : null;

  return (
    <LightboxContext.Provider value={open}>
      {children}
      {state && current && (
        <div
          data-testid="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[200] bg-[#0a0a0a]/95 backdrop-blur-sm flex items-center justify-center px-4 sm:px-12"
          onClick={close}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button
            type="button"
            data-testid="lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            aria-label="Close"
            className="absolute top-5 right-5 sm:top-8 sm:right-8 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white border border-white/20 hover:border-white/50 transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>

          {state.images.length > 1 && (
            <>
              <button
                type="button"
                data-testid="lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                aria-label="Previous image"
                className="hidden sm:flex absolute left-5 sm:left-8 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center text-white/70 hover:text-white border border-white/20 hover:border-white/50 transition-colors duration-200"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                data-testid="lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                aria-label="Next image"
                className="hidden sm:flex absolute right-5 sm:right-8 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center text-white/70 hover:text-white border border-white/20 hover:border-white/50 transition-colors duration-200"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          <div className="relative max-w-[92vw] max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img
                key={current.src}
                src={current.src}
                alt={current.alt || ""}
                data-testid="lightbox-image"
                className="max-w-[92vw] max-h-[75vh] object-contain"
              />
              {!current.noWatermark && <Watermark className="text-xs sm:text-sm" />}
            </div>
            {current.caption && (
              <p data-testid="lightbox-caption" className="mt-4 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-white/70 text-center">
                {current.caption}
              </p>
            )}
            {state.images.length > 1 && (
              <p className="mt-2 text-[11px] text-white/40" data-testid="lightbox-counter">
                {state.index + 1} / {state.images.length}
              </p>
            )}
          </div>
        </div>
      )}
    </LightboxContext.Provider>
  );
}

export function useLightbox() {
  const ctx = useContext(LightboxContext);
  if (!ctx) throw new Error("useLightbox must be used within LightboxProvider");
  return ctx;
}
